/**
 * password-reset.ts — the two PUBLIC calls behind /forgot-password and
 * /reset-password. SPEC-01 §3.7, Stage 2; gateway side in
 * `apps/chaos-gateway/src/identity-lifecycle.ts`.
 *
 * NOT in `./auth.ts`. That module is the signed-in surface (login, me, logout,
 * change-password) and every caller of it already has a session. Everything
 * here is called by somebody who, by definition, cannot sign in — the same
 * audience as `publicInvitationApi` in ./org-invitations.ts, which lives apart
 * for the same reason.
 *
 * ─── THE ONE RULE THIS FILE EXISTS TO KEEP ────────────────────────────────
 *
 * `POST /api/auth/forgot-password` returns ONE answer — 202 and a fixed
 * sentence — for an address that has an account, one that does not, one that
 * is deactivated, and one that has already asked three times today. That is
 * deliberate: any difference between those cases turns the endpoint into a
 * membership oracle, and an attacker with a word list learns who is in the
 * system without ever guessing a password.
 *
 * The gateway keeping that promise is worth nothing if the UI breaks it. So
 * the mapping below is written so that **the uniform answer is the DEFAULT
 * BRANCH, not a recognised success**:
 *
 *     try   { … }               → 'accepted'
 *     catch { …three named…  }  → 'accepted'      ← the fallthrough
 *
 * A gateway that started leaking — a 200 where a 202 was, a `400
 * unknown_email`, a `403 user_inactive` — would still land on 'accepted' here
 * and the person would see the same panel. Building an oracle in this app
 * therefore requires ADDING a named case on purpose; it cannot happen by
 * forgetting one. `tests/unit/password-reset-no-oracle.test.tsx` feeds exactly
 * those leaky shapes in and compares the rendered DOM byte for byte.
 *
 * THE TWO OUTCOMES THAT ARE NOT 'accepted', and why neither is an oracle:
 *
 *   'invalid_email'  400 `invalid_email`. The gateway reaches this by running
 *                    `validateEmail` on the request body BEFORE it touches the
 *                    database, so it is a fact about the caller's own typing
 *                    and cannot vary with who has an account.
 *   'unavailable'    404 `feature_disabled`. First line of the handler, before
 *                    the body is even parsed, so it describes the DEPLOYMENT
 *                    (PASSWORD_RESET_ENABLED) and nothing about any address.
 *
 * WHAT THIS FILE CANNOT FIX: response TIME. A registered address costs the
 * gateway a mail send and an unregistered one does not. That channel lives in
 * identity-lifecycle.ts and is out of reach from here; noted so the next
 * reader does not mistake this comment for a claim of timing safety.
 */
import { ApiError, apiJson, isUnverifiedFailure } from './client';

/**
 * The sentence the gateway answers with, repeated here as the one the screen
 * shows. Copied rather than read out of the response ON PURPOSE: a screen that
 * renders whatever text the server sent would faithfully render a leak the day
 * the server started producing one, and the DOM-equality proof would go green
 * on two different sentences.
 */
export const RESET_REQUEST_ACCEPTED_MESSAGE =
  'If that address has an account, a reset link is on its way.';

/** Mirrors the gateway's ONE password policy (`validatePassword` in auth.ts). */
export const PASSWORD_RULE =
  'At least 14 characters, with an upper case letter, a lower case letter, a digit and a symbol.';

export type ForgotPasswordOutcome =
  /** The uniform answer. Says NOTHING about whether the address has an account. */
  | 'accepted'
  /** Syntactically not an address — a fact about the caller's own typing. */
  | 'invalid_email'
  /** PASSWORD_RESET_ENABLED is off on this deployment. */
  | 'unavailable'
  /** We never found out: network down, timeout, 5xx, offline service worker. */
  | 'unverified';

/**
 * Ask for a reset link.
 *
 * Never throws and never rejects: the caller has no branch to write for a
 * thrown error, and a `catch` at the call site is the second place an oracle
 * could grow.
 */
export async function requestPasswordReset(email: string): Promise<ForgotPasswordOutcome> {
  try {
    await apiJson('/auth/forgot-password', { method: 'POST', json: { email } });
    return 'accepted';
  } catch (err) {
    // ── the three named cases, none of which can depend on WHO the address is
    if (err instanceof ApiError && err.status === 404) return 'unavailable';
    if (err instanceof ApiError && err.status === 400 && err.code === 'invalid_email') {
      return 'invalid_email';
    }
    if (isUnverifiedFailure(err)) return 'unverified';
    // ── the fallthrough. Any other refusal the gateway ever invents is folded
    //    back into the uniform answer rather than shown to the person.
    return 'accepted';
  }
}

export type ResetPasswordOutcome =
  /** The password was changed and every session was revoked. */
  | { kind: 'done' }
  /**
   * The link cannot be spent. ONE outcome for four situations — never seen,
   * expired, already used, and (see below) an account that was deactivated
   * between the mint and the spend. The gateway answers `400
   * invalid_or_expired_token` for the first three and will not say which;
   * neither will this.
   */
  | { kind: 'link_dead' }
  /** The chosen password fails the policy. About the caller's own typing. */
  | { kind: 'weak_password'; message: string }
  | { kind: 'rate_limited' }
  | { kind: 'unavailable' }
  | { kind: 'unverified' };

export async function submitNewPassword(input: {
  token: string;
  newPassword: string;
}): Promise<ResetPasswordOutcome> {
  try {
    await apiJson('/auth/reset-password', { method: 'POST', json: input });
    return { kind: 'done' };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { kind: 'unavailable' };
    if (err instanceof ApiError && err.status === 429) return { kind: 'rate_limited' };
    if (err instanceof ApiError && err.status === 400 && err.code === 'weak_password') {
      return { kind: 'weak_password', message: err.message || PASSWORD_RULE };
    }
    if (isUnverifiedFailure(err)) return { kind: 'unverified' };
    // ── the fallthrough, and it is the SAME shape the dead-link cases get.
    //
    // `403 account_inactive` lands here on purpose. It is reachable only by
    // somebody who already held a valid, unspent, unexpired link — so it is
    // not an oracle for an outsider — but it is a distinguishable panel for no
    // benefit: the account was deactivated, there is nothing the person can do
    // about it from this screen, and asking for another link will (correctly)
    // mint nothing because forgot-password only mints for active users. One
    // fewer shape is one fewer thing a future edit can key on.
    return { kind: 'link_dead' };
  }
}
